<template>
  <main class="w-full">
    <header class="mb-2 py-2 px-6 bg-white sticky top-0">
      <h1 class="text-3xl font-bold text-gray-800">Produits</h1>
      <p class="text-gray-600 mb-2">Gérez votre catalogue de produits</p>
      
      <!-- Actions Bar -->
      <div class="flex flex-col sm:flex-row gap-4 mb-6">
        <div class="relative flex-1">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input placeholder="Rechercher un produit..."
            class="pl-10 w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            :value="searchTerm" @input="searchTerm = $event.target.value" />
        </div>
        
        <select
          class="px-3 py-2 border border-gray-300 bg-white rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          :value="filterModel" @change="filterModel = $event.target.value">
          <option value="">Tous les modèles</option>
          <option v-for="model in productModels" :key="model.id" :value="model.id">
            {{ model.name }}
          </option>
        </select>
        
        <button @click="openAddModal"
          class="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition">
          <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          Ajouter un produit
        </button>
      </div>
    </header>
    
    <div class="p-6 lg:p-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="text-center">
          <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          <p class="text-gray-600">Chargement des produits...</p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredProducts.length === 0 && !loading" class="bg-white rounded-xl shadow-md border border-gray-200">
        <div class="p-12 text-center">
          <svg class="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
          </svg>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">
            {{ searchTerm || filterModel ? 'Aucun résultat' : 'Aucun produit' }}
          </h3>
          <p class="text-gray-600 mb-6">
            {{ searchTerm || filterModel ? 'Aucun produit ne correspond à votre recherche' : 'Commencez par créer votre premier produit' }}
          </p>
          <button @click="openAddModal"
            class="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition mx-auto">
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Ajouter un produit
          </button>
        </div>
      </div>

      <!-- Products Table -->
      <div v-else class="bg-white min-h-[70vh] rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="text-left p-4 text-sm font-medium text-gray-800">Produit</th>
                <th class="text-left p-4 text-sm font-medium text-gray-800">Modèle</th>
                <th class="text-left p-4 text-sm font-medium text-gray-800">Prix</th>
                <th class="text-left p-4 text-sm font-medium text-gray-800">Stock</th>
                <th class="text-left p-4 text-sm font-medium text-gray-800">Statut</th>
                <th class="text-left p-4 text-sm font-medium text-gray-800">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in filteredProducts" :key="product.id"
                class="border-b border-gray-200 hover:bg-gray-50 transition">
                <td class="p-4">
                  <div class="flex items-center space-x-3">
                    <div v-if="product.cover_image" class="w-10 h-10">
                      <img :src="`${$store.state.baseUrl}/${product.cover_image}`" :alt="product.name"
                        class="w-full h-full object-cover rounded-lg border border-gray-200" />
                    </div>
                    <div v-else class="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <div>
                      <span class="font-medium text-gray-800">{{ product.name }}</span>
                      <p v-if="product.slug" class="text-xs text-gray-500">/{{ product.slug }}</p>
                    </div>
                  </div>
                </td>
                <td class="p-4 text-sm text-gray-600">
                  {{ getModelName(product.model_id) }}
                </td>
                <td class="p-4 text-sm font-medium text-gray-800">
                  {{ formatPrice(product.price) }}
                </td>
                <td class="p-4 text-sm">
                  <span :class="product.stock_quantity === 0 ? 'text-red-600 font-medium' : 'text-gray-800'">
                    {{ product.stock_quantity === 0 ? 'Rupture' : product.stock_quantity }}
                  </span>
                </td>
                <td class="p-4">
                  <span :class="`px-3 py-1 rounded-full text-xs font-medium ${product.is_active == 1
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                    }`">
                    {{ product.is_active == 1 ? 'Actif' : 'Inactif' }}
                  </span>
                </td>
                <td class="p-4">
                  <div class="flex space-x-2">
                    <button @click="handleEdit(product)"
                      class="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                      <svg class="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button @click="showDeleteModal = { id: product.id, name: product.name }"
                      class="p-2 border border-gray-300 rounded-lg hover:bg-red-50 transition">
                      <svg class="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Product Modal -->
      <div v-if="showModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 transition"
        @click.self="resetForm">
        <div class="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div class="p-6">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-lg font-semibold text-gray-800">
                {{ editingProduct ? 'Modifier le produit' : 'Ajouter un produit' }}
              </h3>
              <button @click="resetForm" class="p-2 hover:bg-gray-100 rounded-lg transition">
                <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <form @submit.prevent="handleSubmit" class="space-y-4">
              <!-- Images Upload -->
              <div>
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Images du produit
                </label>

                <!-- Main Image -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-800 mb-2">
                    Image principale *
                  </label>
                  <div @click="triggerMainImageUpload"
                    class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition">
                    <div v-if="productForm.cover_image_preview">
                      <img :src="productForm.cover_image_preview" class="h-40 w-40 object-cover rounded-lg mx-auto mb-2" />
                      <p class="text-sm text-gray-600">Cliquer pour changer l'image</p>
                    </div>
                    <div v-else-if="productForm.cover_image">
                      <img :src="`${$store.state.baseUrl}/${productForm.cover_image}`" class="h-40 w-40 object-cover rounded-lg mx-auto mb-2" />
                      <p class="text-sm text-gray-600">Cliquer pour changer l'image</p>
                    </div>
                    <div v-else>
                      <svg class="h-10 w-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      <p class="text-sm text-gray-600">Cliquer pour ajouter une image principale</p>
                      <p class="text-xs text-gray-500 mt-1">Taille recommandée: 800x800px</p>
                    </div>
                    <input type="file" ref="mainImageInput" @change="handleMainImageUpload" accept="image/*"
                      class="hidden" />
                  </div>
                </div>

                <!-- Additional Images -->
                <div>
                  <label class="block text-sm font-medium text-gray-800 mb-2">
                    Images supplémentaires
                  </label>
                  <div class="grid grid-cols-3 gap-3 mb-3">
                    <!-- Images existantes -->
                    <div v-for="(image, index) in existingAdditionalImages" :key="`existing-${index}`" class="relative">
                      <img :src="`${$store.state.baseUrl}/${image.image_path}`" class="h-24 w-full object-cover rounded-lg border border-gray-200" />
                      <button @click="removeExistingImage(image.id)" type="button"
                        class="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition">
                        <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                    
                    <!-- Nouvelles images -->
                    <div v-for="(image, index) in productForm.additionalImages" :key="`new-${index}`" class="relative">
                      <img :src="image.preview" class="h-24 w-full object-cover rounded-lg border border-gray-200" />
                      <button @click="removeAdditionalImage(index)" type="button"
                        class="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition">
                        <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div v-if="(existingAdditionalImages.length + productForm.additionalImages.length) < 5"
                    @click="triggerAdditionalImageUpload"
                    class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-500 transition">
                    <svg class="h-5 w-5 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    <p class="text-sm text-gray-600">Ajouter plus d'images</p>
                    <input type="file" ref="additionalImageInput" @change="handleAdditionalImageUpload" accept="image/*"
                      multiple class="hidden" />
                  </div>
                  <p class="text-xs text-gray-500 mt-2">
                    {{ existingAdditionalImages.length + productForm.additionalImages.length }}/5 images
                  </p>
                </div>
              </div>

              <!-- Product Model Selection -->
            

              <!-- Product Name -->
              <div>
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Nom du produit *
                </label>
                <input v-model="productForm.name" required @change="handleNameChange"
                  class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
                
                <div v-if="productForm.name" class="mt-1 text-xs text-gray-500">
                  Slug: {{ generateSlug(productForm.name) }}
                  <input type="hidden" v-model="productForm.slug" />
                </div>
              </div>
                <div>
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Modèle de produit *
                </label>
                <select v-model="productForm.model_id" required
                  class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition">
                  <option value="">Choisir le modèle</option>
                  <option v-for="model in productModels" :key="model.id" :value="model.id">
                    {{ model.name }}
                  </option>
                </select>
              </div>
                <div>
                <label class="block text-sm font-medium text-gray-800 mb-2">
                 Fournissseur du produit *
                </label>
                <select v-model="productForm.supplier_id" required
                  class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition">
                  <option value="">Choisir le fournisseur</option>
                  <option v-for="supp in suppliers" :key="supp.id" :value="supp.id">
                    {{ supp.name }}
                  </option>
                </select>
              </div>
              <!-- Price and Stock -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-800 mb-2">
                    Prix (FCFA) *
                  </label>
                  <input type="number" v-model.number="productForm.price" required min="0" step="0.01"
                    class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-800 mb-2">
                    Stock *
                  </label>
                  <input type="number" v-model.number="productForm.stock_quantity" required min="0"
                    class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
                </div>
              </div>

              <!-- Product Options -->
              <div class="p-4 bg-gray-50 rounded-lg space-y-3">
                <p class="text-sm font-medium text-gray-800">Options du produit</p>
                
                <div class="flex items-center space-x-2">
                  <input type="checkbox" id="hasSize" v-model="productForm.has_size"
                    class="h-4 w-4 text-blue-600 rounded focus:ring-blue-500" />
                  <label for="hasSize" class="text-sm text-gray-800">Gestion des tailles</label>
                </div>
                
                <div class="flex items-center space-x-2">
                  <input type="checkbox" id="hasColor" v-model="productForm.has_color"
                    class="h-4 w-4 text-blue-600 rounded focus:ring-blue-500" />
                  <label for="hasColor" class="text-sm text-gray-800">Gestion des couleurs</label>
                </div>
              </div>

              <!-- Size Options -->
              <div v-if="productForm.has_size">
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Tailles disponibles
                </label>
                <input v-model="currentsize" @keypress="addsize($event)" @keyup="setnull($event)"
                  placeholder="S, M, L, XL ou 38, 39, 40..."
                  class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
                <div class="grid grid-cols-6 gap-2 w-full bg-gray-100 mt-2 p-2 rounded-lg">
                  <span @click="deletesize(size)" v-for="(size, index) in productForm.sizesArray" :key="index"
                    class="relative h-8 border text-center border-gray-400 bg-white flex items-center justify-center rounded cursor-pointer hover:bg-gray-200 transition">
                    {{ size }}
                    <span class="absolute -top-1 -right-1 text-xs text-red-600">×</span>
                  </span>
                </div>
                <!-- <input type="hidden" v-model="productForm.sizes" :value="productForm.sizesArray.join(',')" /> -->
              </div>

              <!-- Color Options -->
              <div v-if="productForm.has_color">
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Couleurs disponibles
                </label>
                <div class="flex items-center space-x-3 mb-2">
                  <input v-model="currentcolor" type="color" class="rounded-md h-10 w-10 cursor-pointer" />
                  <button type="button" @click="addcolor" class="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition">
                    Ajouter la couleur
                  </button>
                </div>
                <div class="grid grid-cols-6 gap-2 w-full bg-gray-100 p-2 rounded-lg">
                  <span @click="deletecolor(color)" v-for="(color, index) in productForm.colorsArray" :key="index"
                    :style="'background:' + color + ';'"
                    class="relative h-8 border border-gray-400 flex items-center justify-center rounded cursor-pointer">
                    <svg class="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </span>
                </div>
                <!-- <input type="hidden" v-model="productForm.colors" :value="productForm.colorsArray.join(',')" /> -->
              </div>

              <!-- Badge -->
              <div>
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Badge (optionnel)
                </label>
                <select v-model="productForm.badge"
                  class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition">
                  <option value="">Aucun badge</option>
                 
                  <option value="nouveau">Nouveau</option>
                  <option value="promotion">Promotion</option>
                  <option value="best_seller">Best Seller</option>
                  <option value="tendance">Tendance</option>
                </select>
              </div>
              <div v-if="productForm.badge === 'promotion'">
                  <label class="block text-sm font-medium text-gray-800 mb-2">
                    Prix  Promotionnel (FCFA) *
                  </label>
                  <input type="number" v-model.number="productForm.promo_price" required min="0" step="0.01"
                    class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
                </div>

              <!-- Description -->
              <div>
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Description
                </label>
                <textarea v-model="productForm.description" rows="3"
                  class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"></textarea>
              </div>

              <!-- Features -->
              <div>
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Caractéristiques (une par ligne)
                </label>
                <textarea v-model="productForm.featuresText" rows="3" placeholder="Ex: Bluetooth 5.0&#10;IPX7&#10;Batterie 20h"
                  class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"></textarea>
                <!-- <input type="hidden" v-model="productForm.features" :value="productForm.featuresText.split('\n').filter(f => f.trim()).join(',')" /> -->
              </div>

              <!-- Status -->
              <div>
                <label class="block text-sm font-medium text-gray-800 mb-2">
                  Statut
                </label>
                <select v-model="productForm.is_active"
                  class="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition">
                  <option :value="1">Actif</option>
                  <option :value="0">Inactif</option>
                </select>
              </div>

              <div v-if="error" class="bg-red-50 text-red-800 p-3 rounded-lg text-sm">
                {{ error }}
              </div>

              <!-- Action Buttons -->
              <div class="flex space-x-3 pt-4">
                <button type="button" @click="resetForm"
                  :disabled="saving"
                  class="flex-1 px-4 py-2 border border-gray-300 bg-white text-gray-800 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  Annuler
                </button>
                <button type="submit"
                  :disabled="saving"
                  class="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  <span v-if="saving" class="flex items-center justify-center">
                    <svg class="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    {{ editingProduct ? 'Enregistrement...' : 'Ajout...' }}
                  </span>
                  <span v-else>
                    {{ editingProduct ? 'Enregistrer' : 'Ajouter' }}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Delete Modal -->
      <div v-if="showDeleteModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 transition"
        @click.self="showDeleteModal = null">
        <div class="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-sm">
          <div class="p-6 text-center">
            <svg class="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              Confirmer la suppression
            </h3>
            <p class="text-gray-600 mb-6">
              Supprimer "{{ showDeleteModal.name }}" ?
            </p>
            <div v-if="deleteError" class="bg-red-50 text-red-800 p-3 rounded-lg text-sm mb-4">
              {{ deleteError }}
            </div>
            <div class="flex space-x-3">
              <button @click="showDeleteModal = null"
                :disabled="deleting"
                class="flex-1 px-4 py-2 border border-gray-300 bg-white text-gray-800 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                Annuler
              </button>
              <button @click="handleDelete"
                :disabled="deleting"
                class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                <span v-if="deleting" class="flex items-center justify-center">
                  <svg class="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  Suppression...
                </span>
                <span v-else>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'ProductsPage',
  
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      searchTerm: '',
      filterModel: '',
      showModal: false,
      showDeleteModal: null,
      editingProduct: null,
      error: null,
      deleteError: null,

      currentcolor: '',
      currentsize: '',
      
      existingAdditionalImages: [],
      
      productForm: {
        id: null,
        name: '',
        model_id: '',
        price: 0,
        promo_price: 0,
        stock_quantity: 0,
        description: '',
        is_active: 1,
        has_size: false,
        has_color: false,
        sizes: '',
        colors: '',
        sizesArray: [],
        colorsArray: [],
        badge: '',
        supplier_id:'',
        features: '',
        featuresText: '',
        slug: '',
        cover_image: null,
        cover_image_preview: null,
        additionalImages: [] // Array of {file: File, preview: string}
      }
    }
  },

  computed: {
    suppliers(){
      return this.$store.state.admin.suppliers
    },
    // Utilisez les produits admin du store
    products() {
      return this.$store.state.admin.products
    },
    
    // Modèles de produit pour le select
    productModels() {
      return this.$store.state.admin.productModels
    },
    
    filteredProducts() {
      const productsList = this.products
      if (!productsList) return []
      
      return productsList.filter(product => {
        const matchesSearch = product.name && 
          product.name.toLowerCase().includes(this.searchTerm.toLowerCase())
        const matchesModel = !this.filterModel || 
          product.model_id == this.filterModel
        
        return matchesSearch && matchesModel
      })
    }
  },

  async mounted() {
    await this.loadData()
  },

  methods: {
    async loadData() {
      try {
        this.loading = true
        // Chargez toutes les données admin depuis le store
        await this.$store.dispatch('fetchAdminData')
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
        this.error = 'Impossible de charger les données. Veuillez réessayer.'
      } finally {
        this.loading = false
      }
    },

    getModelName(modelId) {
      if (!modelId || !this.productModels) return ''
      const model = this.productModels.find(m => m.id == modelId)
      return model ? model.name : ''
    },

    formatPrice(price) {
      return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
    },

    generateSlug(name) {
  return name
    .toLowerCase()
    // Autorise lettres (avec accents), chiffres et tirets
    .replace(/[^a-z0-9àâäéèêëîïôöùûüç-]+/gi, '-')
    // Regroupe tous les tirets multiples en un seul
    .replace(/-+/g, '-')
    // Supprime les tirets au début et à la fin
    .replace(/^-+|-+$/g, '');
}
,
   
handleNameChange(event) {
      const name = event.target.value
      this.productForm.name = name
      
        this.productForm.slug = this.generateSlug(name)
     
    },
    setnull(event) {
      if (event.key === ',') {
        this.currentsize = ''
      }
    },

    addcolor() {
      if (this.currentcolor.trim() !== '') {
        this.productForm.colorsArray.push(this.currentcolor)
        this.currentcolor = ''
      }
    },

    addsize(event) {
      if (event.key === ',' && this.currentsize.trim() !== '') {
        this.productForm.sizesArray.push(this.currentsize)
        this.currentsize = ''
      }
    },

    deletesize(size) {
      this.productForm.sizesArray = this.productForm.sizesArray.filter(siz => siz !== size)
    },

    deletecolor(color) {
      this.productForm.colorsArray = this.productForm.colorsArray.filter(col => col !== color)
    },

    openAddModal() {
      this.editingProduct = null
      this.resetFormData()
      this.showModal = true
      this.error = null
      this.existingAdditionalImages = []
    },

    resetFormData() {
      this.productForm = {
        id: null,
        name: '',
        model_id: '',
        price: 0,
        promo_price: 0,
        supplier_id:'',
        stock_quantity: 0,
        description: '',
        is_active: 1,
        has_size: false,
        has_color: false,
        sizes: '',
        colors: '',
        sizesArray: [],
        colorsArray: [],
        badge: '',
        features: '',
        featuresText: '',
        slug: '',
        cover_image: null,
        cover_image_preview: null,
        additionalImages: []
      }
      this.currentcolor = ''
      this.currentsize = ''
      this.existingAdditionalImages = []
    },

    resetForm() {
      this.showModal = false
      this.editingProduct = null
      this.resetFormData()
      this.error = null
      this.deleteError = null
    },
notify(title,message,type){
          this.$emit('notify',{title:title,message:message,type:type})
    },
    async handleSubmit() {
      try {
        this.saving = true
        this.error = null
        
        // Validation
        if (!this.productForm.name) {
          throw new Error('Le nom du produit est obligatoire')
        }
        
        if (!this.productForm.model_id) {
          throw new Error('Le modèle de produit est obligatoire')
        }
        
        if (this.productForm.price <= 0) {
          throw new Error('Le prix doit être supérieur à 0')
        }
        if (this.productForm.badge==='promotion' && this.productForm.promo_price <= 0) {
          throw new Error('Le prix promotionnel doit être supérieur à 0')
        }
        
        
        if (this.productForm.stock_quantity < 0) {
          throw new Error('Le stock ne peut pas être négatif')
        }

        // Générer le slug si vide
        if (!this.productForm.slug && this.productForm.name) {
          this.productForm.slug = this.generateSlug(this.productForm.name)
        }

        // Convertir les tableaux en chaînes pour l'API
        if (this.productForm.sizesArray.length > 0) {
          this.productForm.sizes = this.productForm.sizesArray.join(',')
        }
        
        if (this.productForm.colorsArray.length > 0) {
          this.productForm.colors = this.productForm.colorsArray.join(',')
        }
        
        if (this.productForm.featuresText) {
          this.productForm.features = this.productForm.featuresText.split('\n')
            .filter(f => f.trim())
            .join(',')
        }

        // Préparez les données pour l'API
        const formData = new FormData()
        
        // Ajoutez tous les champs sauf les fichiers
        // Object.keys(this.productForm).forEach(key => {
        //   if (!['cover_image_preview', 'additionalImages', 'sizesArray', 'colorsArray', 'featuresText'].includes(key)) {
        //     if (this.productForm[key] !== null && this.productForm[key] !== undefined) {
        //       formData.append(key, this.productForm[key])
        //     }
        //   }
        // })
        
        // // Ajoutez l'image principale
       let additionalImages=[];
        if (this.productForm.additionalImages && this.productForm.additionalImages.length > 0) {
          this.productForm.additionalImages.forEach((item, index) => {
            if (item.file) {
              additionalImages.push(item.file)
            }
          })
        }
        let product={
          ...this.productForm,
          additionalImages:additionalImages,
          cover_image_preview:null

        }

        // Appelez l'action du store pour sauvegarder le produit
        const savedProduct = await this.$store.dispatch('saveProduct', product)
        
        // Affichez un message de succès
        this.notify({
          title: 'Succès',
          text: `Produit ${this.editingProduct ? 'modifié' : 'créé'} avec succès`,
          type: 'success'
        })
        
        // Rechargez les données
        await this.loadData()
        
        this.resetForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
        this.error = error.response?.data?.message || error.message || 'Une erreur est survenue lors de la sauvegarde.'
      } finally {
        this.saving = false
      }
    },

    async handleEdit(product) {
      try {
        this.loading = true
        this.editingProduct = product
        
        // Charger les images supplémentaires si nécessaire
        // Note: Vous devrez implémenter cette fonction dans votre API
        // this.existingAdditionalImages = await this.loadProductImages(product.id)
        
        this.productForm = {
          supplier_id:product.supplier_id || '',
          id: product.id,
          name: product.name || '',
          model_id: product.model_id || '',
          price: product.price || 0,
          promo_price: product.promo_price || 0,
          stock_quantity: product.stock_quantity || 0,
          description: product.description || '',
          is_active: product.is_active !== undefined ? product.is_active : 1,
          has_size: product.has_size || false,
          has_color: product.has_color || false,
          sizes: product.sizes || '',
          colors: product.colors || '',
          sizesArray: product.sizes ? product.sizes.split(',').filter(s => s.trim()) : [],
          colorsArray: product.colors ? product.colors.split(',').filter(c => c.trim()) : [],
          badge: product.badge || '',
          features: product.features || '',
          featuresText: product.features ? product.features.split(',').join('\n') : '',
          slug: product.slug || '',
          cover_image: product.cover_image || null,
          cover_image_preview: null,
          additionalImages: []
        }
        
        this.showModal = true
        this.error = null
      } catch (error) {
        console.error('Erreur lors du chargement du produit:', error)
        this.error = 'Impossible de charger les détails du produit.'
      } finally {
        this.loading = false
      }
    },

    async handleDelete() {
      try {
        this.deleting = true
        this.deleteError = null
        
        // Appelez l'action de suppression du store
        await this.$store.dispatch('deleteProduct', this.showDeleteModal.id)
        
        // Affichez un message de succès
        this.notify({
          title: 'Succès',
          text: 'Produit supprimé avec succès',
          type: 'success'
        })
        
        this.showDeleteModal = null
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        this.deleteError = error.response?.data?.message || 'Une erreur est survenue lors de la suppression.'
      } finally {
        this.deleting = false
      }
    },

    // Image Upload Methods
    triggerMainImageUpload() {
      this.$refs.mainImageInput.click()
    },

    triggerAdditionalImageUpload() {
      this.$refs.additionalImageInput.click()
    },

    handleMainImageUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.productForm.cover_image_preview = e.target.result
          this.productForm.cover_image = file
        }
        reader.readAsDataURL(file)
      }
    },

    handleAdditionalImageUpload(event) {
      const files = event.target.files
      if (files) {
        Array.from(files).forEach(file => {
          if (this.existingAdditionalImages.length + this.productForm.additionalImages.length < 5) {
            const reader = new FileReader()
            reader.onload = (e) => {
              this.productForm.additionalImages.push({
                file: file,
                preview: e.target.result
              })
            }
            reader.readAsDataURL(file)
          }
        })
      }
    },

    removeAdditionalImage(index) {
      this.productForm.additionalImages.splice(index, 1)
    },

    async removeExistingImage(imageId) {
      try {
        // Note: Vous devrez implémenter cette fonction dans votre API
        // await this.deleteProductImage(imageId)
        this.existingAdditionalImages = this.existingAdditionalImages.filter(img => img.id !== imageId)
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'image:', error)
        this.error = 'Impossible de supprimer l\'image.'
      }
    }
  }
}
</script>

<style scoped>
input:focus,
select:focus,
textarea:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.transition {
  transition: all 0.2s ease;
}
</style>
